import { useState } from "react";
import axios from "axios";
import { Phone, Home, TreePine, CheckCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, COMPANY } from "./constants";

export default function ContactModal({ isOpen, onClose, serviceType }) {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', address: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...formData, service_type: serviceType, lead_source: "contact_form" });
      setIsSuccess(true);
      toast.success("Thank you! We'll contact you soon.", { description: "Your free estimate request has been received." });
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData({ name: '', phone: '', email: '', address: '', message: '' });
      }, 2000);
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or call us directly." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        data-testid="contact-modal"
        className="bg-sand-100 border-forest-200 max-w-lg mx-4 sm:mx-auto rounded-2xl"
      >
        <DialogHeader className="text-left">
          <DialogTitle className="font-serif text-2xl text-forest-700 flex items-center gap-2">
            {serviceType === 'residential' ? (
              <><Home className="w-6 h-6 text-forest-500" /> Residential Services</>
            ) : (
              <><TreePine className="w-6 h-6 text-forest-500" /> Commercial Services</>
            )}
          </DialogTitle>
          <DialogDescription className="text-stone-600">
            Get your free estimate today! Fill out the form below and we'll contact you within 24 hours.
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-12 text-center" data-testid="success-message">
            <CheckCircle className="w-16 h-16 text-forest-500 mx-auto mb-4" />
            <h3 className="font-serif text-xl text-forest-700 mb-2">Request Received!</h3>
            <p className="text-stone-600">We'll be in touch soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-forest-700 font-medium">Full Name *</Label>
              <Input id="name" name="name" data-testid="input-name" value={formData.name} onChange={handleChange} required placeholder="John Smith" className="border-stone-200 focus:border-forest-500 focus:ring-forest-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-forest-700 font-medium">Phone *</Label>
                <Input id="phone" name="phone" type="tel" data-testid="input-phone" value={formData.phone} onChange={handleChange} required placeholder="(478) 555-1234" className="border-stone-200 focus:border-forest-500 focus:ring-forest-500" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-forest-700 font-medium">Email *</Label>
                <Input id="email" name="email" type="email" data-testid="input-email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" className="border-stone-200 focus:border-forest-500 focus:ring-forest-500" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address" className="text-forest-700 font-medium">Property Address *</Label>
              <Input id="address" name="address" data-testid="input-address" value={formData.address} onChange={handleChange} required placeholder="123 Main St, Macon, GA 31201" className="border-stone-200 focus:border-forest-500 focus:ring-forest-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message" className="text-forest-700 font-medium">Message (Optional)</Label>
              <Textarea id="message" name="message" data-testid="input-message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project..." rows={3} className="border-stone-200 focus:border-forest-500 focus:ring-forest-500 resize-none" />
            </div>
            <div className="pt-4 space-y-3">
              <Button type="submit" data-testid="submit-btn" disabled={isSubmitting} className="w-full bg-forest-500 hover:bg-forest-600 text-white py-6 rounded-full font-medium text-lg transition-all duration-300">
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><span className="spinner"></span>Sending...</span>
                ) : (
                  <span className="flex items-center gap-2"><Send className="w-5 h-5" />Get Free Estimate</span>
                )}
              </Button>
              <div className="text-center">
                <span className="text-stone-500 text-sm">or call us directly</span>
                <a href={COMPANY.phoneLink} data-testid="modal-phone-link" className="flex items-center justify-center gap-2 text-forest-600 hover:text-forest-700 font-medium mt-1">
                  <Phone className="w-4 h-4" />
                  {COMPANY.phone}
                  <span className="text-xs bg-forest-100 text-forest-600 px-2 py-0.5 rounded-full">24/7</span>
                </a>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
